import {
  DestroyRef,
  Directive,
  ElementRef,
  effect,
  inject,
} from '@angular/core';
import { LanguageService } from '../../core/i18n/language.service';

interface AttributeState {
  original: string;
  translated: string;
}

/**
 * Traduit les anciens libellés statiques encore écrits directement dans les
 * templates. Seules les phrases présentes dans le dictionnaire sont touchées :
 * les noms de clients, produits et autres données métier restent inchangés.
 */
@Directive({
  selector: '[lemAutoTranslate]',
  standalone: true,
})
export class AutoTranslateDirective {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly language = inject(LanguageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly originalTexts = new WeakMap<Text, string>();
  private readonly translatedTexts = new WeakMap<Text, string>();
  private readonly attributeStates = new WeakMap<Element, Map<string, AttributeState>>();
  private readonly observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'characterData') {
        this.translateText(mutation.target as Text);
        continue;
      }

      for (const node of mutation.addedNodes) {
        this.translateTree(node);
      }
    }
  });

  constructor() {
    effect(() => {
      this.language.language();
      queueMicrotask(() => this.translateAll());
    });

    queueMicrotask(() => {
      this.translateAll();
      this.observe();
    });

    this.destroyRef.onDestroy(() => this.observer.disconnect());
  }

  private translateAll(): void {
    this.observer.disconnect();
    this.translateTree(this.host);
    this.observe();
  }

  private observe(): void {
    this.observer.observe(this.host, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  private translateTree(node: Node): void {
    if (node.nodeType === Node.TEXT_NODE) {
      this.translateText(node as Text);
      return;
    }

    if (!(node instanceof Element) || this.shouldSkip(node)) {
      return;
    }

    this.translateAttributes(node);
    for (const child of Array.from(node.childNodes)) {
      this.translateTree(child);
    }
  }

  private translateText(node: Text): void {
    const parent = node.parentElement;
    if (!parent || this.shouldSkip(parent)) {
      return;
    }

    const current = node.nodeValue ?? '';
    const previousTranslation = this.translatedTexts.get(node);
    let original = this.originalTexts.get(node);

    if (
      original === undefined
      || (previousTranslation !== undefined && current !== previousTranslation && current !== original)
    ) {
      original = current;
      this.originalTexts.set(node, original);
    }

    const translated = this.language.translate(original);
    this.translatedTexts.set(node, translated);
    if (current !== translated) {
      node.nodeValue = translated;
    }
  }

  private translateAttributes(element: Element): void {
    const attributes = ['placeholder', 'title', 'aria-label', 'alt'] as const;
    let states = this.attributeStates.get(element);
    if (!states) {
      states = new Map<string, AttributeState>();
      this.attributeStates.set(element, states);
    }

    for (const attribute of attributes) {
      const current = element.getAttribute(attribute);
      if (current === null) {
        continue;
      }

      const previous = states.get(attribute);
      let original = previous?.original ?? current;
      if (previous && current !== previous.translated && current !== previous.original) {
        original = current;
      }

      const translated = this.language.translate(original);
      states.set(attribute, { original, translated });
      if (current !== translated) {
        element.setAttribute(attribute, translated);
      }
    }
  }

  private shouldSkip(element: Element): boolean {
    return Boolean(
      element.closest('[data-no-translate], script, style, code, pre, [contenteditable="true"]'),
    );
  }
}
